import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { SUPPORTED_LOCALES } from '@/lib/i18n'
import { minorUnits } from '@/lib/money'

const INPUT =
  'w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary ' +
  'transition duration-[var(--dur-fast)] placeholder:text-content-muted ' +
  'focus:border-champagne-400/60'

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string
  hint?: string | undefined
  children: ReactNode
  className?: string | undefined
}) {
  return (
    <label className={cn('block', className)}>
      <span className="text-caption uppercase tracking-wide text-content-muted">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint ? <p className="mt-1.5 text-xs text-content-muted">{hint}</p> : null}
    </label>
  )
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
  className,
  maxLength,
  list,
}: {
  label: string
  hint?: string
  value: string
  onChange: (value: string) => void
  type?: 'text' | 'email' | 'tel' | 'url' | 'datetime-local' | 'date' | 'time'
  placeholder?: string
  className?: string
  maxLength?: number
  /** L'identifiant d'une `<datalist>` : des suggestions, la saisie reste libre. */
  list?: string
}) {
  return (
    <Field label={label} hint={hint} className={className}>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        list={list}
        onChange={(event) => onChange(event.target.value)}
        className={INPUT}
      />
    </Field>
  )
}

export function TextAreaField({
  label,
  hint,
  value,
  onChange,
  rows = 4,
}: {
  label: string
  hint?: string
  value: string
  onChange: (value: string) => void
  rows?: number
}) {
  return (
    <Field label={label} hint={hint}>
      <textarea
        value={value}
        rows={rows}
        onChange={(event) => onChange(event.target.value)}
        className={INPUT}
      />
    </Field>
  )
}

export function NumberField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  disabled = false,
}: {
  label: string
  hint?: string
  value: number | null
  onChange: (value: number | null) => void
  min?: number
  max?: number
  disabled?: boolean
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        type="number"
        inputMode="numeric"
        value={value ?? ''}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(event) => {
          const raw = event.target.value
          // Champ vidé : `null`, pas `0`. Un maximum de participants remis à
          // zéro fermerait la vente ; laissé vide, il veut dire « pas de
          // limite ».
          onChange(raw === '' ? null : Number(raw))
        }}
        className={cn(INPUT, 'tabular-nums', disabled && 'cursor-not-allowed opacity-60')}
      />
    </Field>
  )
}

/**
 * Un montant, saisi en euros et stocké en centimes.
 *
 * Le back-end ne manipule que des entiers en unité mineure (RULE : jamais de
 * flottant sur de l'argent). La conversion est donc faite ici, au bord de
 * l'interface, et une seule fois — pas dans chaque écran qui affiche un prix.
 *
 * `Math.round` est indispensable : `19.99 * 100` vaut `1998.9999...` en
 * virgule flottante. Sans arrondi, un plat à 19,99 € serait enregistré à
 * 19,98 €.
 */
export function MoneyField({
  label,
  hint,
  minor,
  currency,
  onChange,
}: {
  label: string
  hint?: string
  minor: number | null
  currency: string
  onChange: (minor: number | null) => void
}) {
  const digits = minorUnits(currency)
  const [draft, setDraft] = useState<string | null>(null)

  const shown = draft ?? (minor === null ? '' : (minor / 10 ** digits).toFixed(digits))

  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-2">
        <input
          inputMode="decimal"
          value={shown}
          // On garde la frappe telle quelle tant que le champ a le focus :
          // reformater à chaque touche empêcherait d'écrire « 19,9 » avant
          // le dernier chiffre.
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            const normalised = (draft ?? '').replace(',', '.').trim()
            setDraft(null)
            if (normalised === '') return onChange(null)
            const parsed = Number(normalised)
            if (!Number.isFinite(parsed)) return
            onChange(Math.round(parsed * 10 ** digits))
          }}
          className={cn(INPUT, 'tabular-nums')}
        />
        <span className="shrink-0 text-sm text-content-muted">{currency}</span>
      </div>
    </Field>
  )
}

export function SwitchField({
  label,
  hint,
  value,
  onChange,
  disabled = false,
}: {
  label: string
  hint?: string
  value: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-6 rounded-md border border-line-subtle bg-bg-elevated px-4 py-3">
      <div>
        <p className="text-sm text-content-primary">{label}</p>
        {hint ? <p className="mt-1 text-xs text-content-muted">{hint}</p> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={cn(
          'relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition duration-[var(--dur-base)]',
          // L'interrupteur reste fin à l'œil, mais le pouce vise une zone de
          // 44 px : le pseudo-élément élargit la cible sans rien déplacer.
          "after:absolute after:-inset-x-1 after:-inset-y-2.5 after:content-['']",
          value ? 'bg-ember' : 'bg-bg-overlay border border-line',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span
          className={cn(
            'absolute top-1 h-4 w-4 rounded-full bg-white transition-transform duration-[var(--dur-base)] ease-[var(--ease-out)]',
            value ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </button>
    </div>
  )
}

export function SelectField<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string
  hint?: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
  disabled?: boolean
}) {
  return (
    <Field label={label} hint={hint}>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        disabled={disabled}
        className={cn(INPUT, disabled && 'cursor-not-allowed opacity-60')}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  )
}

export type I18nValue = Record<string, string>

/**
 * Un texte métier, dans les trois langues.
 *
 * C'est le champ le plus important du back-office : **tout** ce que voit un
 * visiteur est stocké en `{de, fr, en}`. Un onglet par langue plutôt que trois
 * champs empilés — un formulaire d'édition compte une dizaine de textes, et
 * trente champs à l'écran rendraient la page illisible.
 *
 * La pastille sur l'onglet dit d'un coup d'œil ce qui reste à traduire. Sans
 * elle, on publie sans s'apercevoir qu'une langue est vide, et le visiteur
 * allemand tombe sur du français.
 */
export function I18nField({
  label,
  hint,
  value,
  onChange,
  multiline = false,
  locales = SUPPORTED_LOCALES as readonly string[],
}: {
  label: string
  hint?: string
  value: I18nValue
  onChange: (value: I18nValue) => void
  multiline?: boolean
  locales?: readonly string[]
}) {
  const { t } = useTranslation()
  const [active, setActive] = useState(locales[0] ?? 'de')

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <span className="min-w-0 text-caption uppercase tracking-wide text-content-muted">
          {label}
        </span>
        <div className="flex overflow-hidden rounded-full border border-line">
          {locales.map((locale) => {
            const filled = Boolean(value[locale]?.trim())
            return (
              <button
                key={locale}
                type="button"
                onClick={() => setActive(locale)}
                aria-current={locale === active}
                title={filled ? undefined : t('admin.missingTranslation')}
                className={cn(
                  'flex min-h-10 items-center gap-1.5 px-3 py-1 text-xs uppercase transition sm:min-h-0',
                  locale === active
                    ? 'bg-bg-overlay text-content-primary'
                    : 'text-content-muted hover:text-content-secondary',
                )}
              >
                {locale}
                <span
                  aria-hidden
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    filled ? 'bg-state-success' : 'bg-state-warning',
                  )}
                />
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-1.5">
        {multiline ? (
          <textarea
            rows={4}
            value={value[active] ?? ''}
            onChange={(event) => onChange({ ...value, [active]: event.target.value })}
            className={INPUT}
          />
        ) : (
          <input
            value={value[active] ?? ''}
            onChange={(event) => onChange({ ...value, [active]: event.target.value })}
            className={INPUT}
          />
        )}
      </div>
      {hint ? <p className="mt-1.5 text-xs text-content-muted">{hint}</p> : null}
    </div>
  )
}

/** Regroupe des champs sous un titre — une page longue reste parcourable. */
export function FieldGroup({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <section className="rounded-lg border border-line-subtle bg-bg-elevated p-6">
      <h2 className="font-display text-heading-md text-content-primary">{title}</h2>
      {description ? (
        <p className="mt-1 text-sm text-content-secondary">{description}</p>
      ) : null}
      <div className="mt-5 grid gap-5 sm:grid-cols-2">{children}</div>
    </section>
  )
}
